import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { FLOWHUB_MENU_URL } from "../../lib/flowhub";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ext = { target: "_blank", rel: "noopener noreferrer" };

// Soft pastel pedestal moods on light bg.
const MOOD = {
  Sativa: { glow: "from-amber-200/80 via-orange-200/40", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  "Sativa-dom": { glow: "from-amber-200/80 via-orange-200/40", chip: "text-amber-700 bg-amber-50 border-amber-200" },
  Indica: { glow: "from-fuchsia-200/80 via-purple-200/40", chip: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  "Indica-dom": { glow: "from-fuchsia-200/80 via-purple-200/40", chip: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200" },
  "50/50": { glow: "from-emerald-200/80 via-teal-200/40", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  Hybrid: { glow: "from-emerald-200/80 via-teal-200/40", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  "Hybrid 50/50": { glow: "from-emerald-200/80 via-teal-200/40", chip: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  CBD: { glow: "from-sky-200/80 via-blue-200/40", chip: "text-sky-700 bg-sky-50 border-sky-200" },
};
const DEFAULT_MOOD = { glow: "from-brand/30 via-brand/10", chip: "text-brand bg-brand/5 border-brand/20" };

function EditorialCard({ product, index }) {
  const mood = MOOD[product.species] || DEFAULT_MOOD;
  const number = String(index + 1).padStart(2, "0");

  return (
    <a
      href={product.url}
      {...ext}
      className="group relative shrink-0 snap-start flex flex-col w-[280px] sm:w-[320px] lg:w-[360px]"
      data-testid={`home-product-${index}`}
    >
      {/* Pedestal — floating product on light bg */}
      <div className="relative h-[360px] sm:h-[400px] lg:h-[440px] flex items-end justify-center overflow-hidden">
        {/* Huge tabular index number floating in the background — subtle on white */}
        <div className="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
          <span
            className="font-serif italic text-[160px] sm:text-[180px] lg:text-[220px] leading-none tracking-tighter text-ink/[0.04] group-hover:text-ink/[0.07] transition-colors duration-700 select-none"
            style={{ fontFeatureSettings: '"tnum"' }}
          >
            {number}
          </span>
        </div>

        {/* Mood glow pedestal beneath the product */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[88%] h-[60%] pointer-events-none">
          <div
            className={`absolute inset-0 bg-gradient-radial ${mood.glow} to-transparent rounded-[50%] blur-3xl opacity-100 group-hover:opacity-100 transition-opacity duration-500`}
          />
        </div>

        {/* The product image — no frame, just floats */}
        <img
          src={product.image}
          alt={`${product.name}${product.brand ? ` by ${product.brand}` : ""}`}
          loading="lazy"
          className="relative z-10 w-[78%] h-auto max-h-[85%] object-contain drop-shadow-[0_30px_30px_rgba(10,10,10,0.18)] group-hover:translate-y-[-8px] group-hover:scale-[1.03] transition-transform duration-700 ease-out"
        />

        {/* Thin baseline shelf */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%] h-px bg-gradient-to-r from-transparent via-ink/15 to-transparent" />
      </div>

      {/* Editorial info row — light theme */}
      <div className="relative pt-6 pb-2 pl-1">
        <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.3em] mb-2">
          <span className="text-ink/40">— {product.category || "Cannabis"}</span>
          {product.species && (
            <>
              <span className="w-1 h-1 rounded-full bg-ink/20" />
              <span className={`${mood.chip} border px-2 py-0.5`}>{product.species}</span>
            </>
          )}
        </div>

        <h3 className="font-serif text-2xl sm:text-[26px] leading-[1.1] tracking-tight text-ink group-hover:text-brand transition-colors duration-300 line-clamp-2 min-h-[3.6rem]">
          {product.name}
        </h3>

        {product.brand && (
          <div className="mt-2 text-sm text-ink/50 italic font-serif">
            by {product.brand}
          </div>
        )}

        <div className="mt-5 flex items-end justify-between">
          <div className="flex items-baseline gap-2">
            {product.price && (
              <span
                className="font-serif text-4xl sm:text-5xl text-ink tracking-tight leading-none"
                style={{ fontFeatureSettings: '"tnum"' }}
              >
                {product.price}
              </span>
            )}
            {product.size && (
              <span className="text-ink/45 text-[11px] uppercase tracking-[0.25em] leading-none pb-1.5">
                /{product.size}
              </span>
            )}
            {product.thc && (
              <span className="text-ink/45 text-[11px] uppercase tracking-[0.2em] leading-none pb-1.5 ml-2 hidden lg:inline">
                · {product.thc} THC
              </span>
            )}
          </div>
          <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-brand opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
            Shop
            <ArrowUpRight size={12} />
          </div>
        </div>
      </div>
    </a>
  );
}

export default function FeaturedProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ source: null, count: 0, fetched_at: 0 });
  const scrollerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${BACKEND}/api/menu`);
        const json = await res.json();
        if (mounted) {
          setProducts(json.products || []);
          setMeta({ source: json.source, count: json.count, fetched_at: json.fetched_at });
        }
      } catch {
        /* silent */
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const featured = useMemo(() => {
    const withImages = products.filter((p) => p.image && p.price);
    const byCategory = {};
    for (const p of withImages) {
      const key = (p.category || "Other").toLowerCase();
      if (!byCategory[key]) byCategory[key] = [];
      byCategory[key].push(p);
    }
    const out = [];
    let added = true;
    while (out.length < 8 && added) {
      added = false;
      for (const key of Object.keys(byCategory)) {
        if (byCategory[key].length && out.length < 8) {
          out.push(byCategory[key].shift());
          added = true;
        }
      }
    }
    return out;
  }, [products]);

  const refreshedAt = useMemo(() => {
    if (!meta.fetched_at) return null;
    const d = new Date(meta.fetched_at * 1000);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  }, [meta.fetched_at]);

  const scrollBy = (delta) => {
    const node = scrollerRef.current;
    if (!node) return;
    node.scrollBy({ left: delta, behavior: "smooth" });
  };

  if (!loading && featured.length === 0) return null;

  return (
    <section
      id="featured-products"
      className="relative bg-white text-ink py-24 sm:py-32 overflow-hidden"
      data-testid="home-featured-products"
    >
      {/* Soft brand color washes for warmth on white */}
      <div className="absolute top-20 left-10 w-[40%] h-[400px] bg-gradient-radial from-brand/8 to-transparent blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[40%] h-[400px] bg-gradient-radial from-fuchsia-200/30 to-transparent blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Editorial heading */}
        <div className="px-6 mb-14 lg:mb-20 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-brand mb-4">
              <span className="w-6 h-px bg-brand" />
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live · From the shelf
            </div>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-ink">
              On our<br />
              <em className="text-brand">shelves</em> today.
            </h2>
            <p className="mt-6 text-ink/60 text-base sm:text-lg leading-relaxed max-w-lg">
              An editorial cut of what&rsquo;s moving through our Georgia Ave
              floor right now. Synced live from the dispensary — what you see
              is what&rsquo;s stocked.
            </p>
            {refreshedAt && (
              <p className="mt-4 text-[10px] uppercase tracking-[0.3em] text-ink/35">
                Last sync · {refreshedAt}
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <Link
              to="/menu"
              className="inline-flex items-center gap-2 self-start text-[12px] uppercase tracking-[0.3em] text-ink hover:text-brand border-b border-ink/20 hover:border-brand pb-1 transition-colors"
              data-testid="home-featured-view-all"
            >
              The full menu
              <ArrowUpRight size={14} />
            </Link>
            <div className="hidden sm:flex items-center gap-1.5">
              <button
                onClick={() => scrollBy(-380)}
                aria-label="Previous"
                className="w-11 h-11 border border-ink/15 hover:border-brand hover:bg-brand hover:text-white text-ink flex items-center justify-center transition-all"
                data-testid="home-featured-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => scrollBy(380)}
                aria-label="Next"
                className="w-11 h-11 border border-ink/15 hover:border-brand hover:bg-brand hover:text-white text-ink flex items-center justify-center transition-all"
                data-testid="home-featured-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>

        {loading && (
          <div
            className="flex flex-col items-center justify-center py-24 text-ink/50"
            data-testid="home-featured-loading"
          >
            <Loader2 size={26} className="text-brand animate-spin mb-3" />
            <p className="text-[10px] uppercase tracking-[0.3em]">
              Loading live inventory…
            </p>
          </div>
        )}

        {!loading && featured.length > 0 && (
          <div
            ref={scrollerRef}
            className="flex gap-8 sm:gap-12 lg:gap-16 overflow-x-auto snap-x snap-mandatory pb-4 pl-6 pr-6 lg:pl-10 lg:pr-10 scroll-smooth"
            style={{
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
            data-testid="home-featured-scroller"
          >
            <style>{`
              [data-testid="home-featured-scroller"]::-webkit-scrollbar { display: none; }
            `}</style>
            {featured.map((p, i) => (
              <EditorialCard key={p.url + i} product={p} index={i} />
            ))}
            <Link
              to="/menu"
              className="shrink-0 snap-start group flex flex-col items-center justify-center w-[280px] sm:w-[320px] lg:w-[360px] h-[360px] sm:h-[400px] lg:h-[440px] border border-dashed border-ink/15 hover:border-brand hover:bg-brand/5 transition-all"
              data-testid="home-featured-see-all-card"
            >
              <div className="font-serif italic text-7xl text-ink/15 group-hover:text-brand transition-colors">
                +{Math.max(0, (meta.count || 0) - featured.length)}
              </div>
              <div className="mt-4 text-[11px] uppercase tracking-[0.3em] text-ink/60 group-hover:text-brand transition-colors">
                More on the menu
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.3em] text-brand">
                Browse all
                <ArrowUpRight size={12} />
              </div>
            </Link>
          </div>
        )}

        {!loading && featured.length > 0 && (
          <div className="sm:hidden px-6 mt-10">
            <Link
              to="/menu"
              className="w-full inline-flex items-center justify-center gap-2 bg-brand text-white px-6 py-4 text-[11px] uppercase tracking-[0.3em] font-bold"
              data-testid="home-featured-mobile-cta"
            >
              View full menu
              <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
